/**
 * Minimal hand-rolled Bot Framework activity types and their normalization
 * into the channels core's {@link ChannelMessage} (spec §7.1.2). No botbuilder:
 * only the fields the adapter actually reads are typed.
 */
import type { IdentityPort } from "../../src/ports.js";
import type { ChannelMentionEntity } from "../../src/mention/normalize.js";
import { normalizeMentions } from "../../src/mention/normalize.js";
import type { ChannelActor, ChannelMessage, ConversationKey, MessageRef } from "../../src/types.js";
import { makeConversationKey } from "../../src/types.js";

/** The Teams `conversation.id` thread separator: `<channelId>;messageid=<rootId>`. */
const MESSAGE_ID_SEPARATOR = ";messageid=";

/** A Bot Framework activity, reduced to the fields the Teams adapter consumes. */
export type TeamsActivity = {
  /** "message", "messageReaction", "conversationUpdate", … */
  type: string;
  id: string;
  timestamp?: string;
  serviceUrl: string;
  /** Always "msteams" for Teams traffic. */
  channelId: string;
  from: { id: string; name?: string; aadObjectId?: string };
  conversation: { id: string; tenantId?: string; conversationType?: string };
  /** The bot the activity was addressed to. */
  recipient: { id: string; name?: string };
  text?: string;
  entities?: Array<{ type: string; mentioned?: { id: string; name?: string }; text?: string }>;
  replyToId?: string;
  /** Action.Submit payload, e.g. `{ option }` from a question card. */
  value?: unknown;
  channelData?: { tenant?: { id: string } };
};

/**
 * `"19:x@thread;messageid=123"` → key `msteams:19:x@thread/123`; no messageid →
 * channel-level key.
 */
export function conversationKeyFromActivity(a: TeamsActivity): ConversationKey {
  const sep = a.conversation.id.indexOf(MESSAGE_ID_SEPARATOR);
  if (sep === -1) return makeConversationKey("msteams", a.conversation.id);
  return makeConversationKey(
    "msteams",
    a.conversation.id.slice(0, sep),
    a.conversation.id.slice(sep + MESSAGE_ID_SEPARATOR.length),
  );
}

/** Teams tenant id — `conversation.tenantId` with the channelData fallback. */
export function tenantIdOfActivity(a: TeamsActivity): string | undefined {
  return a.conversation.tenantId ?? a.channelData?.tenant?.id;
}

/** The sending user as a {@link ChannelActor} (AAD object id preferred as externalId). */
export function actorOfActivity(a: TeamsActivity): ChannelActor {
  return {
    platform: "msteams",
    externalId: a.from.aadObjectId ?? a.from.id,
    displayName: a.from.name ?? "",
    tenantId: tenantIdOfActivity(a),
  };
}

/**
 * Replaces each mention entity's `<at>…</at>` token in the text with its
 * display name and records the entity's position in the stripped text — so
 * downstream Mention offsets stay meaningful.
 */
function stripMentionTokens(a: TeamsActivity): {
  text: string;
  entities: ChannelMentionEntity[];
} {
  let text = a.text ?? "";
  const entities: ChannelMentionEntity[] = [];
  for (const e of a.entities ?? []) {
    if (e.type !== "mention" || !e.mentioned) continue;
    const displayName = e.mentioned.name ?? "";
    let offset = 0;
    if (e.text) {
      const at = text.indexOf(e.text);
      if (at !== -1) {
        text = text.slice(0, at) + displayName + text.slice(at + e.text.length);
        offset = at;
      }
    }
    entities.push({
      externalId: e.mentioned.id,
      displayName,
      isBot: e.mentioned.id === a.recipient.id,
      offset,
      length: displayName.length,
    });
  }
  return { text, entities };
}

/** The Action.Submit `option` string, when the activity's value carries one. */
function optionOf(value: unknown): string | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const option = (value as { option?: unknown }).option;
  return typeof option === "string" ? option : undefined;
}

/**
 * Normalizes a message activity into a ChannelMessage (spec §7.1.2):
 * payload { type:"prompt", prompt: textWithMentionTokensStripped },
 * meta.channel { platform:"msteams", conversationKey, eventId: a.id, tenantId,
 * actor, messageRef }, meta.mentions via normalize (bot recipient id ⇒ isBot
 * entity). A text-less Action.Submit (question-card answer) uses its `option`
 * as the prompt. Returns null for non-message activities and for the bot's own
 * messages (a.from.id === a.recipient.id — the adapter-level loop filter,
 * spec §8).
 */
export async function activityToChannelMessage(
  a: TeamsActivity,
  identity: IdentityPort,
): Promise<ChannelMessage | null> {
  if (a.type !== "message") return null;
  if (a.from.id === a.recipient.id) return null;

  const conversationKey = conversationKeyFromActivity(a);
  const { text, entities } = stripMentionTokens(a);
  const mentions = await normalizeMentions(
    entities,
    (e) => ({
      platform: "msteams",
      externalId: e.externalId,
      displayName: e.displayName,
      tenantId: tenantIdOfActivity(a),
    }),
    identity,
  );

  const actor = actorOfActivity(a);
  const messageRef: MessageRef = {
    platform: "msteams",
    conversationKey,
    messageId: a.id,
    raw: { serviceUrl: a.serviceUrl, conversationId: a.conversation.id },
  };
  return {
    id: crypto.randomUUID(),
    // seq/sessionId are assigned on the session side; ingress carries placeholders.
    seq: 0,
    timestamp: a.timestamp ?? new Date().toISOString(),
    sessionId: "",
    payload: { type: "prompt", prompt: text || (optionOf(a.value) ?? "") },
    meta: {
      mentions,
      channel: {
        platform: "msteams",
        conversationKey,
        eventId: a.id,
        tenantId: tenantIdOfActivity(a),
        actor: { externalId: actor.externalId, displayName: actor.displayName },
        messageRef,
      },
    },
  };
}
